import React, {FC, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {FormNameEnum} from 'filters-selections';
import {Form} from 'forms';
import {IWithDisabled} from 'models/common';
import {RESOURCE_FIELD_TOOLTIPS} from '../../../utils/tooltipConstants';
import {ItemWithPlaceholder} from '../ItemWithPlaceholder';
import {
  filtersBehavioralIndicatorsEntitiesCollectionSelector,
  filtersCharacterTraitsEntitiesCollectionSelector,
  filtersEthnicitiesEntitiesCollectionSelector,
  filtersGendersEntitiesCollectionSelector,
  filtersImageSourcesEntitiesCollectionSelector,
  filtersPublishedOutputToolsEntitiesCollectionSelector,
} from 'filters-selections/store';

type Props = IWithDisabled;

/** Options come from page-level prefetch (`usePrefetchResourceMetadataLookups`); do not fetch on mount/click. */

export const ImageSourcesMultiSelect: FC<Props> = ({disabled}) => {
  const imageSources = useSelector(filtersImageSourcesEntitiesCollectionSelector);

  const options = useMemo(
    () =>
      imageSources
        .map((source) => ({value: source?.id, label: source?.name}))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})),
    [imageSources]
  );

  return (
    <ItemWithPlaceholder label="Image Source" hasItems infoText={RESOURCE_FIELD_TOOLTIPS.imageSource}>
      <Form.SelectAutocomplete
        name={FormNameEnum.imageSources}
        options={options}
        multiple
        disabled={disabled}
        noOptionsText={options.length ? 'No options' : 'Loading...'}
      />
    </ItemWithPlaceholder>
  );
};

export const PublishedOutputToolSelect: FC<Props> = ({disabled}) => {
  const publishedOutputTools = useSelector(filtersPublishedOutputToolsEntitiesCollectionSelector);

  const options = useMemo(
    () =>
      publishedOutputTools
        .map((tool) => ({value: tool?.id, label: tool?.name}))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})),
    [publishedOutputTools]
  );

  return (
    <ItemWithPlaceholder
      label="Published Output Tool"
      hasItems={!!options.length}
      infoText={RESOURCE_FIELD_TOOLTIPS.publishedOutputTool}
    >
      <Form.Select name={FormNameEnum.publishedOutputTool} options={options} disabled={disabled} />
    </ItemWithPlaceholder>
  );
};

export const EthnicitiesMultiSelect: FC<Props> = ({disabled}) => {
  const ethnicities = useSelector(filtersEthnicitiesEntitiesCollectionSelector);

  const options = useMemo(
    () =>
      ethnicities
        .map((ethnicity) => ({value: ethnicity?.id, label: ethnicity?.name}))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})),
    [ethnicities]
  );

  return (
    <ItemWithPlaceholder label="Perceived Ethnicity" hasItems>
      <Form.SelectAutocomplete
        name={FormNameEnum.ethnicities}
        options={options}
        multiple
        allowDuplicateSelections
        withCheckboxRenderOption={false}
        disabled={disabled}
        noOptionsText={options.length ? 'No options' : 'Loading...'}
      />
    </ItemWithPlaceholder>
  );
};

export const GendersMultiSelect: FC<Props> = ({disabled}) => {
  const genders = useSelector(filtersGendersEntitiesCollectionSelector);

  const options = useMemo(
    () =>
      genders
        .map((gender) => ({value: gender?.id, label: gender?.name}))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})),
    [genders]
  );

  return (
    <ItemWithPlaceholder label="Gender" hasItems>
      <Form.SelectAutocomplete
        name={FormNameEnum.genders}
        options={options}
        multiple
        allowDuplicateSelections
        withCheckboxRenderOption={false}
        disabled={disabled}
        noOptionsText={options.length ? 'No options' : 'Loading...'}
      />
    </ItemWithPlaceholder>
  );
};

export const CharacterTraitsMultiSelect: FC<Props> = ({disabled}) => {
  const characterTraits = useSelector(filtersCharacterTraitsEntitiesCollectionSelector);

  const options = useMemo(() => {
    const flattened = characterTraits.flatMap((trait) =>
      (trait.groupings ?? []).map((grouping) => ({
        value: trait.id,
        label: trait.name,
        group: grouping.name,
      }))
    );

    return flattened.sort(
      (a, b) =>
        (a.group ?? '').localeCompare(b.group ?? '', undefined, {sensitivity: 'base'}) ||
        (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})
    );
  }, [characterTraits]);

  return (
    <ItemWithPlaceholder label="Character Trait" hasItems infoText={RESOURCE_FIELD_TOOLTIPS.characterTrait}>
      <Form.SelectAutocomplete
        name={FormNameEnum.characterTraits}
        options={options}
        multiple
        disabled={disabled}
        noOptionsText={options.length ? 'No options' : 'Loading...'}
        groupBy={(option) => option.group ?? ''}
      />
    </ItemWithPlaceholder>
  );
};

export const BehavioralIndicatorsMultiSelect: FC<Props> = ({disabled}) => {
  const behavioralIndicatorGroups = useSelector(filtersBehavioralIndicatorsEntitiesCollectionSelector);

  const options = useMemo(() => {
    const flattened = behavioralIndicatorGroups.flatMap((group) =>
      (group.indicators ?? []).map((indicator) => ({
        value: indicator.id,
        label: indicator.name,
        group: group.name,
      }))
    );

    return flattened.sort(
      (a, b) =>
        (a.group ?? '').localeCompare(b.group ?? '', undefined, {sensitivity: 'base'}) ||
        (a.label ?? '').localeCompare(b.label ?? '', undefined, {sensitivity: 'base'})
    );
  }, [behavioralIndicatorGroups]);

  return (
    <ItemWithPlaceholder label="Behavioral Indicators" hasItems infoText={RESOURCE_FIELD_TOOLTIPS.behavioralIndicators}>
      <Form.SelectAutocomplete
        name={FormNameEnum.behavioralIndicators}
        options={options}
        multiple
        disabled={disabled}
        noOptionsText={options.length ? 'No options' : 'Loading...'}
        groupBy={(option) => option.group ?? ''}
      />
    </ItemWithPlaceholder>
  );
};
